import { sql } from 'drizzle-orm'
import {
  DB,
  DBNeonHttp,
  PostgresExecuteResult,
  isDBNeonHttpResult,
} from './queries'
import { getTeamSpaceName, parseSpaceName } from '../replicache'
import { invariant } from '@epic-web/invariant'

export type SpaceLineage = string[]

export const getSpaceLineage = async ({
  db,
  cache,
  ...rest
}: {
  db: DB | DBNeonHttp
  cache?: {
    revalidate?: boolean
    get: (key: string) => Promise<SpaceLineage>
    set: (key: string, value: SpaceLineage) => Promise<void>
  }
} & ({ spaceID: string } | { repoId: string })): Promise<SpaceLineage> => {
  const cacheKey = `space-lineage:${
    'spaceID' in rest ? rest.spaceID : rest.repoId
  }`

  if (cache && !cache.revalidate) {
    const cached = await cache.get(cacheKey)
    if (cached) return cached
  }

  let teamSpaceId: string | undefined
  let repoId: string

  if ('spaceID' in rest) {
    const parsed = parseSpaceName(rest.spaceID)
    invariant(parsed.spaceType === 'repo', 'Expected repo space')

    teamSpaceId = getTeamSpaceName(parsed.teamId)
    repoId = parsed.repoId
  } else {
    repoId = rest.repoId
  }

  let result: PostgresExecuteResult = []

  let query = sql`
  WITH RECURSIVE repo_lineage AS (
  -- Base case: start with the given repository
  SELECT 
    spaceid,
    key,
    value,
    value->'forkStatus'->>'fromRepoId' as parentId,
    1 AS depth,
    'team/' || SPLIT_PART(spaceid, '/', 2) || '/repo/' || SPLIT_PART(key, '/', 2) AS lineage
  FROM entry
  WHERE key like 'repository/%' and key = 'repository/' || ${repoId} and value->>'deletedAt' IS NULL
  `

  if (teamSpaceId) {
    query = query.append(sql`AND spaceid = ${teamSpaceId}`)
  }

  query = query.append(sql`
    UNION ALL

  -- Recursive case: find the parent repository
  SELECT 
    e.spaceid,
    e.key,
    e.value,
    e.value->'forkStatus'->>'fromRepoId' as parentId,
    rl.depth + 1,
    rl.lineage || ',' || 'team/' || SPLIT_PART(e.spaceid, '/', 2) || '/repo/' || SPLIT_PART(e.key, '/', 2)
  FROM repo_lineage rl
  JOIN entry e ON e.key like 'repository/%' and e.key = 'repository/' || parentId and e.value->>'deletedAt' IS NULL
  WHERE rl.depth < 100  -- Safeguard against potential circular references
)
SELECT lineage
FROM repo_lineage
ORDER BY depth DESC
LIMIT 1`)

  const _result = await db.execute(query)

  if (isDBNeonHttpResult(_result)) {
    result = _result.rows
  } else {
    result = _result
  }

  const lineage = (result[0]?.lineage as string | undefined)
    ?.split(',')
    .map((s) => s.trim()) as SpaceLineage | undefined

  if (cache && lineage) {
    await cache.set(cacheKey, lineage)
  }

  return lineage ?? []
}
